import { useState } from 'react';
import { formatDistanceToNow, subHours } from 'date-fns';
import {
  Activity,
  AlertCircle,
  Check,
  Clock,
  DollarSign,
  Pencil,
  RefreshCw,
  TrendingUp,
  X,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import {
  usePayFeeSchedule,
  useUpdatePayTenantFee,
  type ClinicPayFeeRow,
} from '@/hooks/usePayTenant';
import { Button } from '@/components/ui/Button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  useExternalCallLogList,
  useExternalCallLogStats,
  type ExternalCallLogRow,
  type TargetSystem,
} from '@/hooks/useExternalCallLog';
import { PageLayout } from '@/components/ui/PageLayout';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SYSTEM_LABELS: Record<TargetSystem, string> = {
  clinio_pay: 'Clinio Pay',
  clinio_forms: 'Clinio Forms',
};

const SYSTEM_COLORS: Record<TargetSystem, string> = {
  clinio_pay: 'bg-ds-teal-tint text-ds-teal',
  clinio_forms: 'bg-ds-teal-tint text-ds-teal',
};

const STATUS_COLORS: Record<number, string> = {
  200: 'var(--green)',
  300: 'var(--teal)',
  400: 'var(--amber)',
  500: 'var(--red)',
};

function getStatusBadgeClass(status: number | null): string {
  if (status === null) return 'bg-ds-neutral-tint text-ds-muted';
  if (status >= 200 && status < 300) return 'bg-ds-green-tint text-ds-green-ink';
  if (status >= 300 && status < 400) return 'bg-ds-teal-tint text-ds-teal';
  if (status >= 400 && status < 500) return 'bg-ds-amber-tint text-ds-amber-ink';
  return 'bg-ds-red-tint text-ds-red-ink';
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatErrorRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Health card
// ---------------------------------------------------------------------------

function HealthCard({
  system,
  healthRow,
  isLoading,
}: {
  system: TargetSystem;
  healthRow: ExternalCallLogRow | null;
  isLoading: boolean;
}) {
  const isHealthy =
    healthRow !== null &&
    healthRow.http_status !== null &&
    healthRow.http_status >= 200 &&
    healthRow.http_status < 300;

  return (
    <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2.5 ${SYSTEM_COLORS[system]}`}>
          <Activity className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ds-text">{SYSTEM_LABELS[system]}</h3>
          <p className="text-xs text-ds-muted">Health check</p>
        </div>
        <div className="ml-auto">
          {isLoading ? (
            <RefreshCw className="h-4 w-4 animate-spin text-ds-muted" />
          ) : healthRow === null ? (
            <span className="flex items-center gap-1.5 rounded-full bg-ds-neutral-tint px-2.5 py-1 text-xs text-ds-muted">
              <WifiOff className="h-3.5 w-3.5" />
              No data yet
            </span>
          ) : isHealthy ? (
            <span className="flex items-center gap-1.5 rounded-full bg-ds-green-tint px-2.5 py-1 text-xs text-ds-green-ink">
              <Wifi className="h-3.5 w-3.5" />
              {healthRow.http_status} OK
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-full bg-ds-red-tint px-2.5 py-1 text-xs text-ds-red-ink">
              <WifiOff className="h-3.5 w-3.5" />
              {healthRow.http_status ?? 'Error'}
            </span>
          )}
        </div>
      </div>

      {healthRow && (
        <div className="mt-3 flex items-center gap-4 border-t border-ds-line pt-3 text-xs text-ds-muted">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatDistanceToNow(new Date(healthRow.created_at), { addSuffix: true })}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            {formatDuration(healthRow.duration_ms)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats panel
// ---------------------------------------------------------------------------

function StatsPanel({
  targetSystem,
  direction,
  since,
}: {
  targetSystem: TargetSystem;
  direction: 'outbound' | 'inbound';
  since: string;
}) {
  const { data: stats, isLoading } = useExternalCallLogStats({ targetSystem, since });

  // Recharts expects fill as a color string, not a Tailwind class
  const rechartsData = (stats?.statusBuckets ?? []).map((b) => {
    return {
      name: b.status !== null ? `${b.status}xx` : 'Err',
      count: b.count,
      fill: b.status !== null ? (STATUS_COLORS[b.status] ?? 'var(--muted)') : 'var(--muted)',
    };
  });

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center py-6">
        <RefreshCw className="h-5 w-5 animate-spin text-ds-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-ds-sunk p-3">
          <p className="text-xs text-ds-muted">Total</p>
          <p className="mt-0.5 text-lg font-semibold text-ds-text">{stats?.totalCount ?? 0}</p>
        </div>
        <div className="rounded-lg bg-ds-sunk p-3">
          <p className="text-xs text-ds-muted">p95 latency</p>
          <p className="mt-0.5 text-lg font-semibold text-ds-text">
            {formatDuration(stats?.p95DurationMs ?? null)}
          </p>
        </div>
        <div className="rounded-lg bg-ds-sunk p-3">
          <p className="text-xs text-ds-muted">Error rate</p>
          <p
            className={`mt-0.5 text-lg font-semibold ${
              (stats?.errorRate ?? 0) > 0.05 ? 'text-ds-red' : 'text-ds-green'
            }`}
          >
            {formatErrorRate(stats?.errorRate ?? 0)}
          </p>
        </div>
      </div>

      {/* Status distribution chart */}
      {rechartsData.length > 0 ? (
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rechartsData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value) => [value as number, 'calls']}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {rechartsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="py-4 text-center text-xs text-ds-muted">
          No {direction} calls in the last 24 h
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call log table
// ---------------------------------------------------------------------------

function CallLogTable({
  rows,
  isLoading,
  emptyLabel,
}: {
  rows: ExternalCallLogRow[];
  isLoading: boolean;
  emptyLabel: string;
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <RefreshCw className="h-5 w-5 animate-spin text-ds-muted" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-ds-muted">{emptyLabel}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-ds-line">
      <table className="min-w-full divide-y divide-ds-line text-xs">
        <thead className="bg-ds-sunk">
          <tr>
            {['Time', 'Method', 'Endpoint', 'Status', 'Duration', 'Error'].map((h) => (
              <th key={h} className="px-3 py-2.5 text-left font-medium text-ds-muted">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ds-line bg-ds-card">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-ds-row-hover">
              <td className="whitespace-nowrap px-3 py-2 text-ds-muted">
                {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-ds-text">
                {row.http_method}
              </td>
              <td
                className="max-w-[220px] truncate px-3 py-2 font-mono text-ds-text"
                title={row.endpoint}
              >
                {row.endpoint}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <span
                  className={`inline-flex rounded px-1.5 py-0.5 font-semibold ${getStatusBadgeClass(row.http_status)}`}
                >
                  {row.http_status ?? '—'}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-ds-muted">
                {formatDuration(row.duration_ms)}
              </td>
              <td
                className="max-w-[180px] truncate px-3 py-2 text-ds-muted"
                title={row.error_message ?? ''}
              >
                {row.error_code ? (
                  <span className="text-ds-red">{row.error_code}</span>
                ) : (
                  <span className="text-ds-green">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-system section
// ---------------------------------------------------------------------------

function SystemSection({ system }: { system: TargetSystem }) {
  const since = subHours(new Date(), 24).toISOString();

  const { data: outboundRows = [], isLoading: outLoading } = useExternalCallLogList({
    targetSystem: system,
    direction: 'outbound',
    limit: 50,
  });

  const { data: inboundRows = [], isLoading: inLoading } = useExternalCallLogList({
    targetSystem: system,
    direction: 'inbound',
    limit: 50,
  });

  // Last /health call
  const healthRow = outboundRows.find((r) => r.endpoint.includes('/health')) ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-ds-text">{SYSTEM_LABELS[system]}</h2>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${SYSTEM_COLORS[system]}`}>
          integration
        </span>
      </div>

      {/* Health card */}
      <HealthCard system={system} healthRow={healthRow} isLoading={outLoading} />

      {/* Outbound */}
      <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-ds-muted" />
          <h3 className="text-sm font-semibold text-ds-text">Last 50 outbound calls</h3>
          <span className="ml-auto text-xs text-ds-muted">last 24 h stats</span>
        </div>
        <StatsPanel targetSystem={system} direction="outbound" since={since} />
        <div className="mt-4">
          <CallLogTable
            rows={outboundRows}
            isLoading={outLoading}
            emptyLabel="No outbound calls recorded yet"
          />
        </div>
      </div>

      {/* Inbound */}
      <div className="rounded-xl border border-ds-line bg-ds-card p-5 shadow-card">
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-4 w-4 text-ds-muted" />
          <h3 className="text-sm font-semibold text-ds-text">Last 50 inbound webhooks</h3>
          <span className="ml-auto text-xs text-ds-muted">last 24 h stats</span>
        </div>
        <StatsPanel targetSystem={system} direction="inbound" since={since} />
        <div className="mt-4">
          <CallLogTable
            rows={inboundRows}
            isLoading={inLoading}
            emptyLabel="No inbound webhooks recorded yet"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule — confirmation modal
// ---------------------------------------------------------------------------

function FeeConfirmModal({
  row,
  newFee,
  onConfirm,
  onCancel,
  isLoading,
}: {
  row: ClinicPayFeeRow;
  newFee: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim p-4">
      <div className="w-full max-w-md rounded-xl border border-ds-line-strong bg-ds-card p-6 shadow-pop">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-lg bg-ds-amber-tint p-2.5">
            <AlertCircle className="h-5 w-5 text-ds-amber" />
          </div>
          <h3 className="text-lg font-semibold text-ds-text">Confirm fee change</h3>
          <Button
            type="button"
            variant="tertiary"
            size="icon"
            className="ml-auto"
            onClick={onCancel}
            aria-label="Close"
          >
            <X />
          </Button>
        </div>

        <p className="text-sm text-ds-muted">
          You are changing the platform fee for{' '}
          <span className="font-semibold text-ds-text">{row.clinic_name}</span> from{' '}
          <span className="font-mono font-medium text-ds-text">
            {row.platform_fee_percent.toFixed(2)}%
          </span>{' '}
          to <span className="font-mono font-medium text-ds-amber">{newFee.toFixed(2)}%</span>.
        </p>

        <div className="mt-4 rounded-lg border border-ds-amber-line bg-ds-amber-tint px-4 py-3 text-sm text-ds-amber-ink">
          The new fee applies to future payments only. Existing checkouts and in-progress payments
          are not affected.
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="button" className="flex-1" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? <RefreshCw className="animate-spin" /> : null}
            {isLoading ? 'Saving…' : 'Confirm change'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule — editable table row
// ---------------------------------------------------------------------------

function FeeRow({ row }: { row: ClinicPayFeeRow }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(row.platform_fee_percent.toFixed(2));
  const [confirming, setConfirming] = useState(false);
  const [localFee, setLocalFee] = useState<number | null>(null);

  const { mutate, isPending } = useUpdatePayTenantFee();

  const displayFee = localFee ?? row.platform_fee_percent;

  function handleEditClick() {
    setInputVal(displayFee.toFixed(2));
    setEditing(true);
  }

  function handleSaveClick() {
    const parsed = parseFloat(inputVal);
    if (isNaN(parsed) || parsed < 0 || parsed > 50) return;
    setConfirming(true);
  }

  function handleConfirm() {
    const newFee = parseFloat(inputVal);
    mutate(
      { clinic_id: row.clinic_id, platform_fee_percent: newFee },
      {
        onSuccess: () => {
          setLocalFee(newFee);
          setEditing(false);
          setConfirming(false);
        },
        onError: () => {
          setConfirming(false);
        },
      },
    );
  }

  const parsedInput = parseFloat(inputVal);
  const inputValid = !isNaN(parsedInput) && parsedInput >= 0 && parsedInput <= 50;

  return (
    <>
      {confirming && (
        <FeeConfirmModal
          row={{ ...row, platform_fee_percent: displayFee }}
          newFee={parsedInput}
          onConfirm={handleConfirm}
          onCancel={() => setConfirming(false)}
          isLoading={isPending}
        />
      )}
      <tr className="hover:bg-ds-row-hover">
        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-ds-text">
          {row.clinic_name}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-sm">
          {editing ? (
            <input
              type="number"
              min="0"
              max="50"
              step="0.01"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              className="ds-field w-24 text-right font-mono"
              autoFocus
            />
          ) : (
            <span className="font-mono font-medium text-ds-text">{displayFee.toFixed(2)}%</span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-xs text-ds-muted">
          {row.last_changed_at
            ? formatDistanceToNow(new Date(row.last_changed_at), { addSuffix: true })
            : '—'}
        </td>
        <td className="whitespace-nowrap px-4 py-3">
          {editing ? (
            <div className="flex items-center gap-2">
              <Button type="button" onClick={handleSaveClick} disabled={!inputValid}>
                <Check />
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
                <X />
                Cancel
              </Button>
            </div>
          ) : (
            <Button type="button" variant="link" onClick={handleEditClick}>
              <Pencil />
              Edit
            </Button>
          )}
        </td>
      </tr>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule section
// ---------------------------------------------------------------------------

function FeeScheduleSection() {
  const { data: rows = [], isLoading, error } = usePayFeeSchedule();

  return (
    <div className="rounded-xl border border-ds-line bg-ds-card shadow-card">
      <div className="flex items-center gap-3 border-b border-ds-line px-5 py-4">
        <div className="rounded-lg bg-ds-amber-tint p-2.5">
          <DollarSign className="h-5 w-5 text-ds-amber" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-ds-text">Per-clinic fee schedule</h2>
          <p className="text-xs text-ds-muted">
            Platform fee charged on every Clinio Pay transaction. Default 5.00%. Changes apply to
            future payments only.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-5 w-5 animate-spin text-ds-muted" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 px-5 py-4 text-sm text-ds-red">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Could not load fee schedule. {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-ds-muted">
          No active Pay tenants found.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ds-line text-sm">
            <thead className="bg-ds-sunk">
              <tr>
                {['Clinic', 'Platform fee', 'Last changed', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-ds-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line bg-ds-card">
              {rows.map((row) => (
                <FeeRow key={row.clinic_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SYSTEMS: TargetSystem[] = ['clinio_pay', 'clinio_forms'];

export function Integrations() {
  const [activeSystem, setActiveSystem] = useState<TargetSystem>('clinio_pay');

  return (
    <PageLayout
      title="Integrations"
      subtitle="Cross-system observability — every outbound call and inbound webhook from the last 90 days. Read-only."
    >

      {/* Per-clinic fee schedule */}
      <div className="mb-8">
        <FeeScheduleSection />
      </div>

      {/* System tabs */}
      <div className="mb-6 flex gap-2 border-b border-ds-line">
        {SYSTEMS.map((sys) => (
          <button
            key={sys}
            onClick={() => setActiveSystem(sys)}
            className={`-mb-px px-4 pb-3 pt-2 text-sm font-medium transition-colors ${
              activeSystem === sys
                ? 'border-b-2 border-ds-teal text-ds-teal'
                : 'text-ds-muted hover:text-ds-text'
            }`}
          >
            {SYSTEM_LABELS[sys]}
          </button>
        ))}
      </div>

      {/* Active system section */}
      <SystemSection key={activeSystem} system={activeSystem} />
    </PageLayout>
  );
}
